import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { usePreferences } from '../contexts/PreferencesContext';
import axios from 'axios';
import { 
  ArrowLeft, CheckCircle, XCircle, RotateCcw, 
  Trophy, Star, BookMarked, Trash2
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Review = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { language } = useLanguage();
  const { scriptMode } = usePreferences();
  
  const [words, setWords] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [isAnswered, setIsAnswered] = useState(false);
  const [score, setScore] = useState(0);
  const [loading, setLoading] = useState(true);
  const [quizMode, setQuizMode] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate('/');
      return;
    }
    fetchWordsToReview();
  }, [token, navigate]);

  const fetchWordsToReview = async () => {
    try {
      const response = await axios.get(`${API}/user/words-to-review`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWords(response.data.words || []);
    } catch (error) {
      console.error('Error fetching words to review:', error);
      toast.error(language === 'en' ? 'Error loading words' : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const removeWord = async (vocabId) => {
    try {
      await axios.delete(`${API}/user/words-to-review/${vocabId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWords(words.filter(w => w.vocab_id !== vocabId));
      toast.success(language === 'en' ? 'Word removed' : 'Mot retiré');
    } catch (error) {
      toast.error(language === 'en' ? 'Error removing word' : 'Erreur');
    }
  };

  const startQuiz = () => {
    if (words.length < 4) {
      toast.error(language === 'en' 
        ? 'Need at least 4 words to start a quiz' 
        : 'Il faut au moins 4 mots pour commencer un quiz');
      return;
    }

    // Generate quiz questions from words to review
    const shuffledWords = [...words].sort(() => Math.random() - 0.5);
    const quizQuestions = shuffledWords.map(word => {
      const vocab = word.vocabulary;
      const otherWords = shuffledWords.filter(w => w.vocab_id !== word.vocab_id);
      
      // Get 3 wrong options
      const wrongOptions = otherWords.slice(0, 3).map(w => ({
        phonetic: w.vocabulary.word_darija_phonetic,
        arabic: w.vocabulary.word_darija_arabic || w.vocabulary.word_darija_phonetic
      }));
      
      const correctOption = {
        phonetic: vocab.word_darija_phonetic,
        arabic: vocab.word_darija_arabic || vocab.word_darija_phonetic
      };
      
      // Build options arrays
      const allOptions = [...wrongOptions, correctOption];
      const shuffledIndices = [0, 1, 2, 3].sort(() => Math.random() - 0.5);
      
      const optionsPhonetic = shuffledIndices.map(i => allOptions[i].phonetic);
      const optionsArabic = shuffledIndices.map(i => allOptions[i].arabic);
      
      const sourceWord = language === 'en' 
        ? (vocab.word_english || vocab.word_french)
        : vocab.word_french;
      
      return {
        vocab_id: word.vocab_id,
        question_text: language === 'en'
          ? `How do you say '${sourceWord}' in Darija?`
          : `Comment dit-on '${sourceWord}' en Darija ?`,
        source_word: sourceWord,
        options_phonetic: optionsPhonetic,
        options_arabic: optionsArabic,
        correct_answer_phonetic: vocab.word_darija_phonetic,
        correct_answer_arabic: vocab.word_darija_arabic || vocab.word_darija_phonetic
      };
    });

    setQuestions(quizQuestions);
    setQuizMode(true);
    setCurrentIndex(0);
    setScore(0);
    setSelectedAnswer(null);
    setIsAnswered(false);
    setShowResults(false);
  };

  const currentQuestion = questions[currentIndex] || {};
  
  // Get options and correct answer based on script mode
  const useArabicScript = scriptMode === 'arabic';
  const questionOptions = useArabicScript 
    ? (currentQuestion.options_arabic || [])
    : (currentQuestion.options_phonetic || []);
  const correctAnswer = useArabicScript
    ? currentQuestion.correct_answer_arabic
    : currentQuestion.correct_answer_phonetic;
  const isCorrect = selectedAnswer === correctAnswer;

  const handleAnswer = async (answer) => {
    if (isAnswered) return;
    
    setSelectedAnswer(answer);
    setIsAnswered(true);
    
    if (answer === correctAnswer) {
      setScore(score + 1);
      // Remove word from review queue when answered correctly
      try {
        await axios.delete(`${API}/user/words-to-review/${currentQuestion.vocab_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setWords(words.filter(w => w.vocab_id !== currentQuestion.vocab_id));
      } catch (error) {
        console.error('Error removing word from review:', error);
      }
    }
  };

  const nextQuestion = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setSelectedAnswer(null);
      setIsAnswered(false);
    } else {
      setShowResults(true);
    }
  };

  const restartQuiz = () => {
    fetchWordsToReview().then(() => {
      setQuizMode(false);
      setShowResults(false);
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Results screen
  if (showResults) {
    const percentage = Math.round((score / questions.length) * 100);
    return (
      <div className="min-h-screen bg-cream">
        <nav className="bg-white border-b border-stone-200 px-4 py-4">
          <button onClick={restartQuiz} className="flex items-center gap-2 text-charcoal hover:text-primary">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-nunito">{language === 'en' ? 'Back' : 'Retour'}</span>
          </button>
        </nav>
        
        <div className="container mx-auto px-4 py-12">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-md mx-auto bg-white rounded-2xl shadow-lg p-8 text-center"
          >
            <Trophy className="w-16 h-16 text-accent mx-auto mb-4" />
            <h2 className="font-playfair text-3xl font-bold text-charcoal mb-2">
              {language === 'en' ? 'Review Complete!' : 'Révision terminée !'}
            </h2>
            <p className="font-nunito text-lg text-charcoal/70 mb-6">
              {language === 'en' 
                ? `You got ${score} out of ${questions.length} correct`
                : `Vous avez ${score} bonnes réponses sur ${questions.length}`}
            </p>
            
            <div className="flex items-center justify-center gap-2 mb-6">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className={`w-8 h-8 ${i < Math.ceil(percentage / 20) ? 'text-accent fill-accent' : 'text-stone-300'}`}
                />
              ))}
            </div>
            
            <p className="font-nunito text-4xl font-bold text-primary mb-8">{percentage}%</p>
            
            <div className="space-y-3">
              {words.length >= 4 && (
                <button
                  onClick={startQuiz}
                  className="w-full py-3 bg-primary text-white rounded-xl font-nunito font-bold flex items-center justify-center gap-2"
                >
                  <RotateCcw className="w-5 h-5" />
                  {language === 'en' ? 'Review Again' : 'Réviser encore'}
                </button>
              )}
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full py-3 bg-stone-100 text-charcoal rounded-xl font-nunito font-bold"
              >
                {language === 'en' ? 'Back to Dashboard' : 'Retour au tableau de bord'}
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // Quiz mode
  if (quizMode && questions.length > 0) {
    return (
      <div className="min-h-screen bg-cream">
        <nav className="bg-white border-b border-stone-200 px-4 py-4">
          <div className="flex items-center justify-between">
            <button onClick={restartQuiz} className="flex items-center gap-2 text-charcoal hover:text-primary">
              <ArrowLeft className="w-5 h-5" />
              <span className="font-nunito">{language === 'en' ? 'Exit' : 'Quitter'}</span>
            </button>
            <span className="font-nunito text-charcoal/70">
              {currentIndex + 1} / {questions.length}
            </span>
          </div>
          
          {/* Progress bar */}
          <div className="mt-3 h-2 bg-stone-200 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-primary"
              initial={{ width: 0 }}
              animate={{ width: `${((currentIndex + 1) / questions.length) * 100}%` }}
            />
          </div>
        </nav>

        <div className="container mx-auto px-4 py-8">
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            className="max-w-lg mx-auto"
          >
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
              <p className="font-nunito text-charcoal/70 mb-6 text-center">
                {currentQuestion.question_text}
              </p>
            </div>

            {/* Options */}
            <div className="grid gap-3">
              {questionOptions.map((option, index) => {
                let buttonClass = 'bg-stone-50 border-stone-200 hover:border-primary hover:bg-primary/5';
                
                if (isAnswered) {
                  if (option === correctAnswer) {
                    buttonClass = 'bg-green-100 border-green-500 text-green-800';
                  } else if (option === selectedAnswer && !isCorrect) {
                    buttonClass = 'bg-red-100 border-red-500 text-red-800';
                  }
                } else if (option === selectedAnswer) {
                  buttonClass = 'bg-primary/10 border-primary';
                }

                return (
                  <motion.button
                    key={index}
                    whileHover={!isAnswered ? { scale: 1.02 } : {}}
                    whileTap={!isAnswered ? { scale: 0.98 } : {}}
                    onClick={() => handleAnswer(option)}
                    disabled={isAnswered}
                    className={`w-full p-4 rounded-xl border-2 ${useArabicScript ? 'font-amiri' : 'font-nunito'} text-lg text-left transition-all ${buttonClass}`}
                  >
                    <div className="flex items-center justify-between">
                      <span>{option}</span>
                      {isAnswered && option === correctAnswer && (
                        <CheckCircle className="w-6 h-6 text-green-600" />
                      )}
                      {isAnswered && option === selectedAnswer && !isCorrect && (
                        <XCircle className="w-6 h-6 text-red-600" />
                      )}
                    </div>
                  </motion.button>
                );
              })}
            </div>

            {/* Feedback and Next */}
            {isAnswered && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-6"
              >
                <div className={`p-4 rounded-xl mb-4 ${isCorrect ? 'bg-green-100' : 'bg-red-100'}`}>
                  <p className={`font-nunito font-bold ${isCorrect ? 'text-green-800' : 'text-red-800'}`}>
                    {isCorrect 
                      ? (language === 'en' ? '✓ Correct! Word removed from review.' : '✓ Correct ! Mot retiré de la révision.') 
                      : (language === 'en' 
                        ? `✗ The answer was: ${correctAnswer}` 
                        : `✗ La réponse était : ${correctAnswer}`)}
                  </p>
                </div>
                
                <button
                  onClick={nextQuestion}
                  className="w-full py-4 bg-primary text-white rounded-xl font-nunito font-bold text-lg flex items-center justify-center gap-2"
                >
                  {currentIndex < questions.length - 1 
                    ? (language === 'en' ? 'Next' : 'Suivant')
                    : (language === 'en' ? 'See Results' : 'Voir les résultats')}
                </button>
              </motion.div>
            )}
          </motion.div>
        </div>
      </div>
    );
  }

  // Word list view
  return (
    <div className="min-h-screen bg-cream">
      <nav className="bg-white border-b border-stone-200 px-4 py-4">
        <div className="flex items-center justify-between">
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 text-charcoal hover:text-primary">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-nunito">{language === 'en' ? 'Back' : 'Retour'}</span>
          </button>
          <div className="flex items-center gap-2">
            <BookMarked className="w-5 h-5 text-primary" />
            <span className="font-nunito font-bold text-charcoal">{words.length}</span>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-lg mx-auto">
          <h1 className="font-playfair text-3xl font-bold text-charcoal mb-2 text-center">
            {language === 'en' ? 'Words to Review' : 'Mots à réviser'}
          </h1>
          <p className="font-nunito text-charcoal/70 text-center mb-6">
            {language === 'en' 
              ? 'Words you got wrong in quizzes'
              : 'Les mots que vous avez ratés dans les quiz'}
          </p>

          {words.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
              <BookMarked className="w-16 h-16 text-stone-300 mx-auto mb-4" />
              <p className="font-nunito text-charcoal/70">
                {language === 'en' 
                  ? 'No words to review! Keep practicing quizzes.'
                  : 'Aucun mot à réviser ! Continuez les quiz.'}
              </p>
            </div>
          ) : (
            <>
              {words.length >= 4 && (
                <button
                  onClick={startQuiz}
                  className="w-full py-4 mb-6 bg-primary text-white rounded-xl font-nunito font-bold text-lg flex items-center justify-center gap-2 shadow-lg"
                >
                  <RotateCcw className="w-5 h-5" />
                  {language === 'en' ? 'Start Review Quiz' : 'Commencer le quiz de révision'}
                </button>
              )}
              
              {words.length < 4 && words.length > 0 && (
                <p className="text-center text-amber-600 font-nunito text-sm mb-4">
                  {language === 'en' 
                    ? `Need ${4 - words.length} more words to start a quiz`
                    : `Il faut encore ${4 - words.length} mot(s) pour commencer un quiz`}
                </p>
              )}

              <div className="space-y-3">
                {words.map((word) => {
                  const vocab = word.vocabulary;
                  return (
                    <motion.div
                      key={word.vocab_id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-white rounded-xl shadow p-4 flex items-center justify-between"
                    >
                      <div>
                        <p className={`${scriptMode === 'arabic' ? 'font-amiri' : 'font-nunito'} text-lg font-bold text-primary`}>
                          {scriptMode === 'arabic' 
                            ? (vocab.word_darija_arabic || vocab.word_darija_phonetic)
                            : vocab.word_darija_phonetic}
                        </p>
                        <p className="font-nunito text-sm text-charcoal/70">
                          {language === 'en' 
                            ? (vocab.word_english || vocab.word_french)
                            : vocab.word_french}
                        </p>
                        <p className="font-nunito text-xs text-charcoal/50 mt-1">
                          {word.category} • {word.level}
                        </p>
                      </div>
                      <button
                        onClick={() => removeWord(word.vocab_id)}
                        className="p-2 text-stone-400 hover:text-red-500 transition-colors"
                        title={language === 'en' ? 'Remove from review' : 'Retirer de la révision'}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </motion.div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Review;
